import { useState, useMemo } from 'react';
import { C } from './constants';
import { copierTexteRobuste } from './App';

// ─────────────────────────────────────────────────────────────────────────────
// 📦 Produits vendus : classement des produits que tu vends le plus, en pourcentages,
// et pour chaque produit la liste des clientes qui l'ont acheté (suivi, promotions...).
// Données lues dans les commandes des clientes (client.commandes[].lignes[]).
// ─────────────────────────────────────────────────────────────────────────────

const sansAccents = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "");
// Deux écritures proches d'un même produit ("Shampoing X" / "shampoing  x") sont regroupées
const cleProduit = (nom) => sansAccents(String(nom || "")).toLowerCase().replace(/\s+/g, " ").trim();
const nomClient = (c) => ((c.prenom || "") + " " + (c.nom || "")).trim() || "Cliente";
const dateFr = (d) => {
  if (!d) return "";
  const x = new Date(d);
  return isNaN(x.getTime()) ? "" : x.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
};
const fmtPct = (v) => (v >= 10 ? Math.round(v) : Math.round(v * 10) / 10).toString().replace(".", ",") + " %";
const fmtEuro = (v) => Math.round(v).toLocaleString("fr-FR") + " €";

// Numéro au format WhatsApp (France par défaut)
export const numWhatsApp = (tel) => {
  let t = String(tel || "").replace(/[^\d+]/g, "");
  if (!t) return "";
  if (t.startsWith("+")) t = t.slice(1);
  else if (t.startsWith("00")) t = t.slice(2);
  else if (t.startsWith("0")) t = "33" + t.slice(1);
  return t.length >= 9 ? t : "";
};

export function agregerProduits(clients, { depuisJours = null, maintenant = new Date() } = {}) {
  const limite = depuisJours ? new Date(maintenant.getTime() - depuisJours * 86400000) : null;
  const produits = new Map();
  let totalLignes = 0;
  const acheteuses = new Set();
  (clients || []).forEach((c) => {
    (c.commandes || []).forEach((cmd) => {
      if (limite) {
        if (!cmd.date) return;
        const d = new Date(cmd.date);
        if (isNaN(d.getTime()) || d < limite) return;
      }
      // Anciennes commandes : pas de "lignes", seulement le texte "produits" séparé par des virgules
      const lignes = Array.isArray(cmd.lignes) && cmd.lignes.length
        ? cmd.lignes
        : (cmd.produits ? String(cmd.produits).split(",").map((n) => ({ nom: n })) : []);
      lignes.forEach((l) => {
        const nom = String((l && l.nom) || "").trim();
        if (!nom) return;
        const cle = cleProduit(nom);
        let p = produits.get(cle);
        if (!p) { p = { cle, noms: {}, ventes: 0, clients: new Map(), ca: 0, avecPrix: 0, dernier: "" }; produits.set(cle, p); }
        p.noms[nom] = (p.noms[nom] || 0) + 1;
        p.ventes++;
        totalLignes++;
        const prix = parseFloat(l.prix);
        if (!isNaN(prix) && prix > 0) { p.ca += prix; p.avecPrix++; }
        const dt = cmd.date || "";
        if (dt > p.dernier) p.dernier = dt;
        let cl = p.clients.get(c.id);
        if (!cl) { cl = { id: c.id, nom: nomClient(c), tel: c.tel || "", achats: 0, dernier: "" }; p.clients.set(c.id, cl); }
        cl.achats++;
        if (dt > cl.dernier) cl.dernier = dt;
        acheteuses.add(c.id);
      });
    });
  });
  const liste = [...produits.values()].map((p) => ({
    ...p,
    nom: Object.entries(p.noms).sort((a, b) => b[1] - a[1])[0][0],
    nbClients: p.clients.size,
    clientsListe: [...p.clients.values()].sort((a, b) => (b.dernier || "").localeCompare(a.dernier || "")),
  }));
  return { liste, totalLignes, nbAcheteuses: acheteuses.size };
}

const PERIODES = [
  { id: "tout", label: "Tout", jours: null },
  { id: "365", label: "12 mois", jours: 365 },
  { id: "90", label: "3 mois", jours: 90 },
  { id: "30", label: "30 jours", jours: 30 },
];
const TRIS = [
  { id: "ventes", label: "Ventes" },
  { id: "clients", label: "Clientes" },
  { id: "ca", label: "CA estimé" },
];

function ClassementProduits({ clients, onOuvrirCliente, onCreerListe }) {
  const [periode, setPeriode] = useState("tout");
  const [tri, setTri] = useState("ventes");
  const [recherche, setRecherche] = useState("");
  const [ouvert, setOuvert] = useState(null);
  const [copie, setCopie] = useState("");

  const jours = (PERIODES.find((p) => p.id === periode) || PERIODES[0]).jours;
  const data = useMemo(() => agregerProduits(clients, { depuisJours: jours }), [clients, jours]);
  const totalClients = (clients || []).length;
  const aDesPrix = data.liste.some((p) => p.avecPrix > 0);

  const liste = useMemo(() => {
    const q = sansAccents(recherche).toLowerCase().trim();
    const filtre = q ? data.liste.filter((p) => cleProduit(p.nom).includes(q)) : data.liste;
    const cle = tri === "clients" ? (p) => p.nbClients : tri === "ca" ? (p) => p.ca : (p) => p.ventes;
    return [...filtre].sort((a, b) => cle(b) - cle(a) || b.ventes - a.ventes || a.nom.localeCompare(b.nom, "fr"));
  }, [data, tri, recherche]);
  const maxVentes = Math.max(1, ...data.liste.map((p) => p.ventes));

  const copierListe = async (p) => {
    const texte = "Clientes ayant acheté : " + p.nom + "\n" +
      p.clientsListe.map((c) => "- " + c.nom + (c.tel ? " : " + c.tel : "")).join("\n");
    const ok = await copierTexteRobuste(texte);
    setCopie(ok ? p.cle : "erreur");
    setTimeout(() => setCopie(""), 2200);
  };

  const chip = (actif) => ({
    padding: ".3rem .7rem", fontSize: ".68rem", fontWeight: 600, borderRadius: 20, cursor: "pointer", fontFamily: "inherit",
    border: `1px solid ${actif ? C.rose : C.pale}`, background: actif ? C.rose : C.blanc, color: actif ? C.blanc : C.gris,
  });

  return (
    <div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1.25rem", fontWeight: 300, color: C.brun, marginBottom: ".2rem" }}>
        Mes produits <em style={{ fontStyle: "italic", color: C.rose }}>les plus vendus</em>
      </div>
      <div style={{ fontSize: ".72rem", color: C.gris, lineHeight: 1.6, marginBottom: ".8rem" }}>
        Classement calculé à partir des commandes enregistrées dans les fiches de tes clientes. Touche un produit pour voir qui l'a acheté.
      </div>

      <div style={{ display: "flex", gap: ".35rem", flexWrap: "wrap", marginBottom: ".7rem" }}>
        {PERIODES.map((p) => (
          <button key={p.id} onClick={() => { setPeriode(p.id); setOuvert(null); }} style={chip(periode === p.id)}>{p.label}</button>
        ))}
      </div>

      {data.liste.length === 0 && (
        <div style={{ textAlign: "center", padding: "2rem 1rem", color: C.gris, fontSize: ".76rem", lineHeight: 1.7, background: C.creme, borderRadius: 12 }}>
          <div style={{ fontSize: "2rem", marginBottom: ".3rem" }}>📦</div>
          {totalClients === 0
            ? "Ajoute d'abord tes clientes."
            : (periode === "tout"
              ? "Aucune commande enregistrée pour l'instant. Ouvre la fiche d'une cliente et ajoute sa commande : ses produits apparaîtront ici."
              : "Aucune commande sur cette période. Essaie « Tout ».")}
        </div>
      )}

      {data.liste.length > 0 && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: ".4rem", marginBottom: ".8rem" }}>
            {[
              { v: data.liste.length, l: "produits différents" },
              { v: data.totalLignes, l: "ventes enregistrées" },
              { v: data.nbAcheteuses, l: "clientes acheteuses" + (totalClients ? " sur " + totalClients : "") },
            ].map((s) => (
              <div key={s.l} style={{ background: C.creme, borderRadius: 10, padding: ".55rem .4rem", textAlign: "center" }}>
                <div style={{ fontFamily: "Georgia,serif", fontSize: "1.2rem", fontWeight: 600, color: C.brun }}>{s.v}</div>
                <div style={{ fontSize: ".56rem", color: C.gris, lineHeight: 1.3 }}>{s.l}</div>
              </div>
            ))}
          </div>

          <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="🔍 Chercher un produit..."
            style={{ width: "100%", boxSizing: "border-box", border: `1px solid ${C.pale}`, borderRadius: 9, padding: ".45rem .7rem", fontSize: ".78rem", fontFamily: "inherit", color: C.texte, background: C.creme, outline: "none", marginBottom: ".5rem" }} />

          <div style={{ display: "flex", gap: ".35rem", alignItems: "center", flexWrap: "wrap", marginBottom: ".7rem" }}>
            <span style={{ fontSize: ".62rem", color: C.gris }}>Trier par :</span>
            {TRIS.filter((t) => t.id !== "ca" || aDesPrix).map((t) => (
              <button key={t.id} onClick={() => setTri(t.id)} style={chip(tri === t.id)}>{t.label}</button>
            ))}
          </div>

          {liste.length === 0 && <div style={{ textAlign: "center", color: C.gris, fontSize: ".74rem", padding: "1rem" }}>Aucun produit ne correspond.</div>}

          {liste.map((p, i) => {
            const rang = i + 1;
            const est = ouvert === p.cle;
            const pctVentes = data.totalLignes ? (p.ventes / data.totalLignes) * 100 : 0;
            const pctClientes = data.nbAcheteuses ? (p.nbClients / data.nbAcheteuses) * 100 : 0;
            return (
              <div key={p.cle} style={{ background: C.blanc, border: `1.5px solid ${est ? C.rose : C.pale}`, borderRadius: 12, marginBottom: ".45rem", overflow: "hidden" }}>
                <div onClick={() => setOuvert(est ? null : p.cle)} style={{ padding: ".65rem .8rem", cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: ".55rem" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: rang <= 3 ? C.or + "30" : C.creme, color: C.brun, fontWeight: 700, fontSize: rang <= 3 ? ".95rem" : ".72rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {rang === 1 ? "🥇" : rang === 2 ? "🥈" : rang === 3 ? "🥉" : rang}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: ".8rem", fontWeight: 700, color: C.brun, lineHeight: 1.3, wordBreak: "break-word" }}>{p.nom}</div>
                      <div style={{ fontSize: ".62rem", color: C.gris, marginTop: ".1rem" }}>
                        {p.ventes} vente{p.ventes > 1 ? "s" : ""} · {p.nbClients} cliente{p.nbClients > 1 ? "s" : ""} ({fmtPct(pctClientes)} de tes clientes acheteuses)
                        {p.avecPrix > 0 ? " · ~" + fmtEuro(p.ca) : ""}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", fontWeight: 600, color: C.rose }}>{fmtPct(pctVentes)}</div>
                      <div style={{ fontSize: ".54rem", color: C.gris }}>des ventes</div>
                    </div>
                    <div style={{ fontSize: ".8rem", color: C.gris, transform: est ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</div>
                  </div>
                  <div style={{ height: 6, background: C.creme, borderRadius: 6, marginTop: ".5rem", overflow: "hidden" }}>
                    <div style={{ width: Math.max(3, (p.ventes / maxVentes) * 100) + "%", height: "100%", background: `linear-gradient(90deg,${C.rose},${C.or})`, borderRadius: 6 }} />
                  </div>
                </div>

                {est && (
                  <div style={{ borderTop: `1px solid ${C.pale}`, padding: ".7rem .8rem", background: C.creme + "80" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".5rem", marginBottom: ".5rem", flexWrap: "wrap" }}>
                      <div style={{ fontSize: ".62rem", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: C.rose }}>
                        👩 {p.nbClients} cliente{p.nbClients > 1 ? "s" : ""} · dernier achat {dateFr(p.dernier) || "—"}
                      </div>
                      <button onClick={() => copierListe(p)}
                        style={{ background: copie === p.cle ? C.vert : C.brun, color: "white", border: "none", borderRadius: 8, padding: ".3rem .7rem", fontSize: ".66rem", fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                        {copie === p.cle ? "✅ Copié !" : copie === "erreur" ? "Copie impossible" : "📋 Copier la liste"}
                      </button>
                    </div>
                    {onCreerListe && (
                      <button onClick={() => onCreerListe(p.nom, p.clientsListe.map((c) => c.nom + (c.tel ? " : " + c.tel : "")))}
                        style={{ width: "100%", background: C.blanc, color: C.brun, border: `1.5px solid ${C.rose}`, borderRadius: 8, padding: ".4rem", fontSize: ".68rem", fontWeight: 700, fontFamily: "inherit", cursor: "pointer", marginBottom: ".5rem" }}>
                        📝 En faire une liste à cocher (pour mon suivi)
                      </button>
                    )}
                    <div style={{ fontSize: ".62rem", color: C.gris, marginBottom: ".5rem", lineHeight: 1.5 }}>
                      Idéal pour une promo, une nouveauté ou un suivi sur ce produit. Touche une cliente pour ouvrir sa fiche, ou le bouton WhatsApp pour lui écrire.
                    </div>
                    {p.clientsListe.map((c) => {
                      const wa = numWhatsApp(c.tel);
                      return (
                        <div key={String(c.id)} style={{ display: "flex", alignItems: "center", gap: ".5rem", background: C.blanc, border: `1px solid ${C.pale}`, borderRadius: 9, padding: ".45rem .65rem", marginBottom: ".3rem" }}>
                          <div onClick={() => onOuvrirCliente && onOuvrirCliente(c.id)} style={{ flex: 1, minWidth: 0, cursor: onOuvrirCliente ? "pointer" : "default" }}>
                            <div style={{ fontSize: ".76rem", fontWeight: 600, color: C.brun }}>{c.nom}</div>
                            <div style={{ fontSize: ".6rem", color: C.gris }}>
                              {c.achats} achat{c.achats > 1 ? "s" : ""}{c.dernier ? " · dernier le " + dateFr(c.dernier) : ""}{c.tel ? " · " + c.tel : ""}
                            </div>
                          </div>
                          {wa && (
                            <a href={"https://wa.me/" + wa} target="_blank" rel="noopener noreferrer"
                              style={{ background: "#25D366", color: "white", borderRadius: 8, padding: ".3rem .55rem", fontSize: ".62rem", fontWeight: 700, textDecoration: "none", flexShrink: 0 }}>
                              💬 WhatsApp
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {aDesPrix && (
            <div style={{ fontSize: ".58rem", color: C.gris, lineHeight: 1.5, marginTop: ".6rem" }}>
              Le CA estimé additionne les prix du catalogue des produits enregistrés dans les commandes : c'est une estimation, pas ton CA exact.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 📝 Mes listes : chaque distributrice crée ses propres listes à cocher, sur ce qu'elle veut
// (produits à commander, clientes à relancer pour une promo, idées, courses...).
// Enregistré dans le champ "db-listes" : [{id,titre,items:[{id,texte,fait}]}]
// ─────────────────────────────────────────────────────────────────────────────
function MesListes({ listes, onSave, ouvrirId, setOuvrirId }) {
  const [titre, setTitre] = useState("");
  const [ajout, setAjout] = useState({});
  const [aSupprimer, setASupprimer] = useState(null);
  const [copie, setCopie] = useState("");

  const toutes = Array.isArray(listes) ? listes : [];
  const maj = (id, fn) => onSave(toutes.map((l) => (l.id === id ? fn(l) : l)));
  const uid2 = () => Date.now() + Math.floor(Math.random() * 1000);

  const creer = () => {
    const t = titre.trim();
    if (!t) return;
    const l = { id: uid2(), titre: t, items: [] };
    onSave([l, ...toutes]);
    setTitre("");
    setOuvrirId(l.id);
  };
  const ajouter = (id) => {
    const lignes = String(ajout[id] || "").split("\n").map((x) => x.replace(/^\s*(?:[-•*·]+|\d+\s*[.)])\s*/, "").trim()).filter(Boolean);
    if (!lignes.length) return;
    maj(id, (l) => ({ ...l, items: [...(l.items || []), ...lignes.map((texte) => ({ id: uid2(), texte, fait: false }))] }));
    setAjout((a) => ({ ...a, [id]: "" }));
  };
  const copier = async (l) => {
    const texte = l.titre + "\n" + (l.items || []).map((i) => (i.fait ? "✅ " : "▫️ ") + i.texte).join("\n");
    const ok = await copierTexteRobuste(texte);
    setCopie(ok ? l.id : "erreur");
    setTimeout(() => setCopie(""), 2200);
  };

  return (
    <div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1.25rem", fontWeight: 300, color: C.brun, marginBottom: ".2rem" }}>
        Mes <em style={{ fontStyle: "italic", color: C.rose }}>listes</em>
      </div>
      <div style={{ fontSize: ".72rem", color: C.gris, lineHeight: 1.6, marginBottom: ".8rem" }}>
        Crée tes propres listes sur ce que tu veux : produits à commander, clientes à relancer pour une promo, idées, rappels... Tu coches au fur et à mesure.
      </div>

      <div style={{ display: "flex", gap: ".4rem", marginBottom: ".9rem" }}>
        <input value={titre} onChange={(e) => setTitre(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") creer(); }}
          placeholder="Nom de la liste (ex : Produits à commander)"
          style={{ flex: 1, minWidth: 0, border: `1px solid ${C.pale}`, borderRadius: 8, padding: ".5rem .65rem", fontSize: ".76rem", fontFamily: "inherit", color: C.texte, background: C.blanc, outline: "none" }} />
        <button onClick={creer}
          style={{ background: titre.trim() ? C.rose : C.pale, color: "white", border: "none", borderRadius: 8, padding: ".5rem .8rem", fontSize: ".72rem", fontWeight: 700, fontFamily: "inherit", cursor: "pointer", flexShrink: 0 }}>
          + Créer
        </button>
      </div>

      {toutes.length === 0 && (
        <div style={{ textAlign: "center", padding: "1.6rem 1rem", color: C.gris, fontSize: ".76rem", lineHeight: 1.7, background: C.creme, borderRadius: 12 }}>
          <div style={{ fontSize: "1.6rem", marginBottom: ".3rem" }}>📝</div>
          Tu n'as pas encore de liste. Donne un nom à ta première liste ci-dessus, puis ajoute-y ce que tu veux.
        </div>
      )}

      {toutes.map((l) => {
        const items = l.items || [];
        const faits = items.filter((i) => i.fait).length;
        const est = ouvrirId === l.id;
        return (
          <div key={l.id} style={{ background: C.blanc, border: `1px solid ${C.pale}`, borderRadius: 12, marginBottom: ".5rem", overflow: "hidden" }}>
            <div onClick={() => setOuvrirId(est ? null : l.id)} style={{ padding: ".7rem .8rem", cursor: "pointer", display: "flex", alignItems: "center", gap: ".6rem" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: ".84rem", fontWeight: 700, color: C.brun, wordBreak: "break-word" }}>{l.titre}</div>
                <div style={{ fontSize: ".62rem", color: C.gris, marginTop: ".1rem" }}>
                  {items.length === 0 ? "Liste vide" : `${faits}/${items.length} fait${faits > 1 ? "s" : ""}`}
                </div>
              </div>
              <div style={{ fontSize: ".8rem", color: C.gris, transform: est ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</div>
            </div>

            {est && (
              <div style={{ borderTop: `1px solid ${C.pale}`, padding: ".7rem .8rem", background: C.creme + "80" }}>
                {items.map((i) => (
                  <div key={i.id} style={{ display: "flex", alignItems: "flex-start", gap: ".5rem", padding: ".35rem 0", borderBottom: `1px solid ${C.pale}` }}>
                    <button onClick={() => maj(l.id, (x) => ({ ...x, items: x.items.map((y) => (y.id === i.id ? { ...y, fait: !y.fait } : y)) }))}
                      aria-label={i.fait ? "Décocher" : "Cocher"}
                      style={{ width: 22, height: 22, flexShrink: 0, borderRadius: 6, border: `1.5px solid ${i.fait ? C.vert : C.rose}`, background: i.fait ? C.vert : C.blanc, color: "white", fontSize: ".75rem", cursor: "pointer", lineHeight: 1, padding: 0 }}>
                      {i.fait ? "✓" : ""}
                    </button>
                    <div style={{ flex: 1, minWidth: 0, fontSize: ".78rem", color: i.fait ? C.gris : C.texte, textDecoration: i.fait ? "line-through" : "none", lineHeight: 1.45, wordBreak: "break-word", paddingTop: 2 }}>{i.texte}</div>
                    <button onClick={() => maj(l.id, (x) => ({ ...x, items: x.items.filter((y) => y.id !== i.id) }))}
                      aria-label="Retirer"
                      style={{ background: "none", border: "none", color: C.gris, fontSize: "1rem", cursor: "pointer", padding: "0 .2rem", lineHeight: 1 }}>×</button>
                  </div>
                ))}

                <textarea value={ajout[l.id] || ""} onChange={(e) => setAjout((a) => ({ ...a, [l.id]: e.target.value }))}
                  placeholder={"Ajoute un élément, ou plusieurs (un par ligne)"}
                  style={{ width: "100%", boxSizing: "border-box", minHeight: 62, marginTop: ".6rem", border: `1px solid ${C.pale}`, borderRadius: 8, padding: ".5rem .6rem", fontSize: ".76rem", fontFamily: "inherit", color: C.texte, background: C.blanc, outline: "none", resize: "vertical", lineHeight: 1.5 }} />
                <button onClick={() => ajouter(l.id)}
                  style={{ width: "100%", marginTop: ".35rem", background: (ajout[l.id] || "").trim() ? C.rose : C.pale, color: "white", border: "none", borderRadius: 8, padding: ".45rem", fontSize: ".72rem", fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                  + Ajouter à la liste
                </button>

                <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap", marginTop: ".6rem" }}>
                  {items.length > 0 && (
                    <button onClick={() => copier(l)}
                      style={{ background: copie === l.id ? C.vert : C.brun, color: "white", border: "none", borderRadius: 8, padding: ".3rem .7rem", fontSize: ".66rem", fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                      {copie === l.id ? "✅ Copié !" : copie === "erreur" ? "Copie impossible" : "📋 Copier"}
                    </button>
                  )}
                  {faits > 0 && (
                    <button onClick={() => maj(l.id, (x) => ({ ...x, items: x.items.filter((y) => !y.fait) }))}
                      style={{ background: C.blanc, color: C.brun, border: `1px solid ${C.pale}`, borderRadius: 8, padding: ".3rem .7rem", fontSize: ".66rem", fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}>
                      🧹 Retirer les éléments cochés
                    </button>
                  )}
                  {aSupprimer === l.id ? (
                    <>
                      <button onClick={() => { onSave(toutes.filter((x) => x.id !== l.id)); setASupprimer(null); setOuvrirId(null); }}
                        style={{ background: "#c0392b", color: "white", border: "none", borderRadius: 8, padding: ".3rem .7rem", fontSize: ".66rem", fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                        Oui, supprimer la liste
                      </button>
                      <button onClick={() => setASupprimer(null)}
                        style={{ background: C.blanc, color: C.gris, border: `1px solid ${C.pale}`, borderRadius: 8, padding: ".3rem .7rem", fontSize: ".66rem", fontFamily: "inherit", cursor: "pointer" }}>
                        Non
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setASupprimer(l.id)}
                      style={{ background: "none", color: C.gris, border: `1px solid ${C.pale}`, borderRadius: 8, padding: ".3rem .7rem", fontSize: ".66rem", fontFamily: "inherit", cursor: "pointer" }}>
                      🗑 Supprimer la liste
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Onglet « 📦 Produits » : deux vues, le classement des ventes et mes listes personnelles
// ─────────────────────────────────────────────────────────────────────────────
export function ProduitsVendusTab({ clients, onOuvrirCliente, listes = [], onSaveListes = () => {} }) {
  const [vue, setVue] = useState("classement");
  const [ouvrirId, setOuvrirId] = useState(null);

  const creerDepuisProduit = (nom, lignes) => {
    const l = { id: Date.now(), titre: "Suivi : " + nom, items: lignes.map((texte, i) => ({ id: Date.now() + i + 1, texte, fait: false })) };
    onSaveListes([l, ...(Array.isArray(listes) ? listes : [])]);
    setOuvrirId(l.id);
    setVue("listes");
  };

  const pill = (actif) => ({
    flex: 1, padding: ".45rem", fontSize: ".72rem", fontWeight: 600, borderRadius: 20, cursor: "pointer", fontFamily: "inherit",
    border: `1px solid ${actif ? C.brun : C.pale}`, background: actif ? C.brun : C.blanc, color: actif ? C.blanc : C.gris,
  });

  return (
    <div>
      <div style={{ display: "flex", gap: ".4rem", marginBottom: ".9rem" }}>
        <button onClick={() => setVue("classement")} style={pill(vue === "classement")}>📊 Classement</button>
        <button onClick={() => setVue("listes")} style={pill(vue === "listes")}>
          📝 Mes listes{Array.isArray(listes) && listes.length ? ` (${listes.length})` : ""}
        </button>
      </div>
      {vue === "classement"
        ? <ClassementProduits clients={clients} onOuvrirCliente={onOuvrirCliente} onCreerListe={creerDepuisProduit} />
        : <MesListes listes={listes} onSave={onSaveListes} ouvrirId={ouvrirId} setOuvrirId={setOuvrirId} />}
    </div>
  );
}
